import React, { useMemo, useState } from 'react';
import C from '../pages/customer/colors';
import SelectionQuotaProgress from '../pages/customer/SelectionQuotaProgress';
import weddingStroll from '../assets/selection-preview/wedding-stroll.jpg';
import portraitFloral from '../assets/selection-preview/portrait-floral.jpg';
import coupleKiss from '../assets/selection-preview/couple-kiss.jpg';
import coupleField from '../assets/selection-preview/couple-field.jpg';
import coupleSunset from '../assets/selection-preview/couple-sunset.jpg';
import portraitEditorial from '../assets/selection-preview/portrait-editorial.jpg';
import { getPictureThumbnailUrl } from '../pictureUrls';
import { formatMoney, multiplyMoney } from '../money';

const SAMPLE_IMAGES = [weddingStroll, portraitFloral, coupleKiss, coupleField, coupleSunset, portraitEditorial];
const MAX_RENDERED_PREVIEW_PICTURES = 18;

function buildSamplePictures(count) {
  return Array.from({ length: count }, (_, index) => ({
    id: `preview-${index + 1}`,
    fileName: `PICSECT_${String(index + 1).padStart(4, '0')}.JPG`,
    publicUrlSmall: SAMPLE_IMAGES[index % SAMPLE_IMAGES.length],
  }));
}

const FILTERS = [
  { value: 'all', label: '全部' },
  { value: 'selected', label: '已选' },
  { value: 'unselected', label: '未选' },
];

function PreviewPhotoTile({ picture, selected, showImageName }) {
  return (
    <div
      data-testid={`customer-photo-${picture.id}`}
      data-selected={selected ? 'true' : 'false'}
      data-preview-only="true"
      aria-hidden="true"
      style={{
        position: 'relative',
        aspectRatio: '4/5',
        padding: 0,
        border: 'none',
        borderRadius: 4,
        overflow: 'hidden',
        cursor: 'default',
        background: C.paper2,
        boxShadow: selected ? `inset 0 0 0 3px ${C.brand}` : 'none',
        userSelect: 'none',
      }}
    >
      <img
        src={getPictureThumbnailUrl(picture)}
        alt=""
        decoding="async"
        style={{
          position: 'absolute', inset: 0,
          width: '100%', height: '100%',
          objectFit: 'cover', objectPosition: 'center',
        }}
      />
      <span style={{
        position: 'absolute', bottom: 7, right: 7,
        width: 23, height: 23, borderRadius: 12,
        background: selected ? C.brand : 'rgba(0,0,0,.35)',
        border: `2px solid ${selected ? C.brand : 'rgba(255,255,255,.7)'}`,
        display: 'grid', placeItems: 'center',
        boxSizing: 'border-box',
        boxShadow: selected ? '0 4px 10px rgba(247,119,176,.45)' : 'none',
      }}>
        {selected && (
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.7" strokeLinecap="round" strokeLinejoin="round">
            <path d="m5 13 4 4L19 7"/>
          </svg>
        )}
      </span>
      {showImageName && (
        <span style={{
          position: 'absolute', bottom: 0, left: 0, right: 0,
          padding: '13px 5px 4px',
          fontFamily: C.mono, fontSize: 7,
          color: '#fff', textAlign: 'left',
          background: 'linear-gradient(transparent, rgba(0,0,0,.56))',
          pointerEvents: 'none',
        }}>
          {picture.fileName}
        </span>
      )}
    </div>
  );
}

export default function SelectionLivePreview({
  showImageName,
  forceLimit,
  price,
  photoCount,
  hasPackage = true,
}) {
  const limit = Math.max(0, Number(photoCount) || 0);
  const unitPrice = Math.max(0, Number(price) || 0);
  const [filter, setFilter] = useState('all');
  const hasLimit = limit > 0;
  const selectedCount = hasLimit ? (forceLimit ? limit : limit + 2) : 0;
  const totalCount = Math.max(6, selectedCount + 4);
  const renderedPictureCount = Math.min(MAX_RENDERED_PREVIEW_PICTURES, totalCount);
  const pictures = useMemo(() => buildSamplePictures(renderedPictureCount), [renderedPictureCount]);
  const renderedSelectedCount = Math.min(selectedCount, Math.max(0, renderedPictureCount - 2));
  const selectedIds = useMemo(
    () => new Set(pictures.slice(0, renderedSelectedCount).map(picture => picture.id)),
    [pictures, renderedSelectedCount]
  );

  const filteredPictures = useMemo(() => pictures.filter(picture => {
    if (filter === 'selected') return selectedIds.has(picture.id);
    if (filter === 'unselected') return !selectedIds.has(picture.id);
    return true;
  }), [filter, pictures, selectedIds]);

  const overLimit = hasLimit ? Math.max(0, selectedCount - limit) : 0;
  const extraCost = multiplyMoney(overLimit, unitPrice);

  const getFilterCount = value => {
    if (value === 'selected') return selectedCount;
    if (value === 'unselected') return totalCount - selectedCount;
    return totalCount;
  };

  return (
    <div
      data-testid="order-selection-live-preview"
      style={{
        position: 'relative',
        height: 600,
        overflow: 'hidden',
        borderRadius: 24,
        border: `1px solid ${C.hair2}`,
        background: C.paper,
        boxShadow: '0 24px 60px -32px rgba(26,22,20,.38)',
        fontFamily: C.sans,
      }}
    >
      <div
        data-testid="preview-mode-banner"
        style={{
          position: 'absolute', top: 10, left: 10, right: 10, zIndex: 2,
          height: 36, padding: '0 13px', borderRadius: 18,
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 9,
          background: C.ink, color: '#fff',
          boxShadow: '0 12px 26px -16px rgba(26,22,20,.75)',
          fontFamily: C.sans, fontSize: 11, fontWeight: 600,
          pointerEvents: 'none',
        }}
      >
        <span style={{
          padding: '3px 7px', borderRadius: 10,
          background: C.brand, color: '#fff',
          fontFamily: C.mono, fontSize: 8, letterSpacing: .8,
        }}>PREVIEW</span>
        <span>预览模式 · 此区域不会提交订单</span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <div style={{
          padding: '57px 15px 12px',
          background: 'rgba(237,230,213,.94)',
          borderBottom: `1px solid ${C.hair}`,
          flexShrink: 0,
        }}>
          <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 12 }}>
            <div style={{ minWidth: 0 }}>
              <div style={{
                fontFamily: C.mono, fontSize: 8, letterSpacing: 1.2,
                color: C.brand, fontWeight: 600, textTransform: 'uppercase',
              }}>
                PICSECT · 选片
              </div>
              <div style={{
                marginTop: 4, fontFamily: C.serif, fontSize: 22,
                lineHeight: 1.05, color: C.ink,
              }}>
                林屿婚礼选片
              </div>
            </div>
            <div data-testid="preview-selection-counter" style={{ flexShrink: 0, textAlign: 'right' }}>
              <div style={{
                fontFamily: C.serif, fontSize: 27, lineHeight: 1,
                color: C.ink, fontVariantNumeric: 'tabular-nums',
              }}>
                {selectedCount}
                <span style={{ color: C.ink3, fontSize: 15 }}>/{limit || '—'}</span>
              </div>
              <div style={{
                marginTop: 3, fontFamily: C.mono, fontSize: 8,
                color: C.ink3, letterSpacing: 1.2,
              }}>
                SELECTED
              </div>
            </div>
          </div>

          <SelectionQuotaProgress
            selectedCount={selectedCount}
            selectionLimit={limit}
            hasPackage={hasPackage}
            forceLimit={Boolean(forceLimit)}
            pricePerPic={unitPrice}
          />
        </div>

        <div style={{
          display: 'flex', alignItems: 'center', gap: 20,
          padding: '7px 14px 6px',
          borderBottom: `1px solid ${C.hair}`,
          background: C.paper, flexShrink: 0,
        }}>
          {FILTERS.map(item => (
            <button
              key={item.value}
              type="button"
              data-testid={`preview-filter-${item.value}`}
              aria-pressed={filter === item.value}
              onClick={() => setFilter(item.value)}
              style={{
                padding: '5px 0 6px', border: 'none',
                borderBottom: `2px solid ${filter === item.value ? C.ink : 'transparent'}`,
                background: 'transparent',
                color: filter === item.value ? C.ink : C.ink3,
                fontFamily: C.sans, fontSize: 11,
                fontWeight: filter === item.value ? 600 : 400,
                cursor: 'pointer',
              }}
            >
              {item.label} <span style={{ fontSize: 9, opacity: .7 }}>{getFilterCount(item.value)}</span>
            </button>
          ))}
        </div>

        <div style={{ flex: 1, overflow: 'auto', padding: 4 }}>
          {filteredPictures.length > 0 ? (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
              gap: 4,
            }}>
              {filteredPictures.map(picture => (
                <PreviewPhotoTile
                  key={picture.id}
                  picture={picture}
                  selected={selectedIds.has(picture.id)}
                  showImageName={showImageName}
                />
              ))}
            </div>
          ) : (
            <div style={{ padding: '46px 0', textAlign: 'center', color: C.ink3, fontSize: 12 }}>
              这里暂时没有照片
            </div>
          )}
        </div>

        <div style={{
          minHeight: 102, padding: '8px 13px 10px',
          display: 'flex', flexDirection: 'column', gap: 7,
          alignItems: 'center', justifyContent: 'center',
          background: 'rgba(237,230,213,.96)',
          borderTop: `1px solid ${C.hair}`,
          flexShrink: 0,
        }}>
          <div style={{
            width: '100%', padding: '7px 10px', boxSizing: 'border-box',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7,
            borderRadius: 10, border: '1px solid rgba(247,119,176,.28)',
            background: 'rgba(247,119,176,.12)',
            fontFamily: C.sans, fontSize: 10.5, color: C.ink2,
          }}>
            <span style={{
              padding: '3px 7px', borderRadius: 10, flexShrink: 0,
              background: C.brand, color: '#fff', fontWeight: 700,
              fontSize: 9, letterSpacing: .5,
            }}>仅预览</span>
            <span>此处不会提交，创建订单后由客户操作</span>
          </div>
          <div
            data-testid="preview-review-selection"
            data-preview-only="true"
            aria-hidden="true"
            style={{
              width: '100%', height: 43, padding: '0 12px',
              borderRadius: 22, border: 'none',
              display: 'flex', alignItems: 'center', gap: 8,
              background: selectedCount === 0
                ? 'rgba(26,22,20,.42)'
                : (overLimit > 0 ? C.brand : C.ink),
              color: '#fff', fontFamily: C.sans, fontSize: 12, fontWeight: 600,
              boxShadow: 'none', opacity: .58,
              cursor: 'not-allowed', pointerEvents: 'none', boxSizing: 'border-box',
            }}
          >
            <span style={{
              width: 29, height: 29, borderRadius: 15,
              background: selectedCount === 0
                ? 'rgba(247,119,176,.25)'
                : (overLimit > 0 ? '#fff' : C.brand),
              color: overLimit > 0 ? C.brand2 : '#fff',
              display: 'grid', placeItems: 'center',
              fontVariantNumeric: 'tabular-nums', flexShrink: 0,
            }}>
              {selectedCount}
            </span>
            <span style={{ flex: 1, textAlign: 'center' }}>
              {selectedCount === 0
                ? '请先选择照片'
                : (overLimit > 0 ? '确认并支付' : '确认选片')}
            </span>
            {overLimit > 0 && <span>¥{formatMoney(extraCost)}</span>}
            <span style={{ opacity: .65 }}>→</span>
          </div>
        </div>
      </div>
    </div>
  );
}
